import { Component, OnInit, OnDestroy, Input } from '@angular/core';
import { CompanyReport } from '../../../redux/models/dashboard/report';
import { tap, map } from 'rxjs/operators';
import { formatNumber, getFilterBySymbol, camelize } from '../../../redux/services/utils/data-helpers';
import {
  toggleTrigger,
  slideTrigger,
  diveTrigger,
  autoHeightTrigger
} from '../../../redux/models/dashboard/animations';
import { DashboardService } from '../../../redux/services/dashboard/dashboard.service';
import { State } from '../../../redux/reducers';
import { State as ReportState } from '../../../redux/reducers/dashboard/reducers-report';
import { Store } from '@ngrx/store';
import { Change, SelectReport } from '../../../redux/actions/dashboard/actions-options';
import { PdfService } from '../../../redux/services/dashboard/pdf.service';
import { DoughnutChartDirective } from '../../../directives/doughnutChart.directive';
import { Observable, Subscription } from 'rxjs';
import { CombinedState as DashboardCombinedState } from '../../../redux/reducers/dashboard/reducers-dashboard';
import { groupingOptions } from '../../../redux/models/dashboard/option-values';

@Component({
  standalone: false,
  selector: 'app-dashboard-right',
  templateUrl: './dashboard-right.component.html',
  styleUrls: ['./dashboard-right.component.scss'],
  animations: [toggleTrigger, diveTrigger, slideTrigger, autoHeightTrigger]
})
export class DashboardRightComponent implements OnInit, OnDestroy {
  @Input() dm = false;

  readonly dashboardDetailsNights: string = $localize`:@@dashboardDetails.reports.Nights:Nights`;
  readonly dashboardDetailsAverageNightCost: string = $localize`:@@dashboardDetails.reports.AverageNightCost:Average night cost`;
  readonly dashboardDetailsBaseTurnover: string = $localize`:@@dashboardDetails.reports.BaseTurnover:Total Revenue`;
  readonly dashboardDetailsFare: string = $localize`:@@dashboardDetails.reports.Fare:Total Revenue excl. Taxes`;
  readonly dashboardDetailsTaxes: string = $localize`:@@dashboardDetails.reports.Taxes:Taxes`;
  readonly dashboardAllConsultants: string = $localize`:@@dashboardDetails.reports.AllConsultants:All consultants`;
  readonly dashboardAllCarriers: string = $localize`:@@dashboardDetails.reports.AllCarriers:All carriers`;


  reportState$: Observable<ReportState> = this.dashboardService.getNewLoadedDashboard().pipe(
    tap(this.updateFromState.bind(this)),
    map(dashboard => dashboard.report)
  );
  hidden = true;
  showDetails = false;
  showOrigins = false;
  selectedRecord: number;
  openedRecord: number;
  symbolType: string;
  sliderHeight = '';
  sliderTargetHeight = '';
  sliderHeightState = 'off';
  formatNumber = formatNumber;
  camelize = camelize;
  doughnutDiveState = 'in';
  grouping = groupingOptions[0].value;
  groupingOptions = [...groupingOptions];
  selectedSubscription: Subscription;
  originsAllRecordsTitle = '';

  constructor(
    private readonly dashboardService: DashboardService,
    private readonly store: Store<State>,
    private readonly pdf: PdfService
  ) {
    this.dashboardService.getNewLoadedDashboard().pipe(
      tap(this.updateFromState.bind(this)),
      map(dashboard => dashboard.report)
    );
  }

  ngOnInit() {
    this.selectedSubscription = this.dashboardService
      .getDashboard()
      .subscribe(state => (this.selectedRecord = state.options.selectedReport));
  }

  ngOnDestroy() {
    this.selectedSubscription.unsubscribe();
  }

  updateReport() {
    this.store.dispatch(new Change({ options: { grouping: this.grouping } }));
  }

  getChartValues(records: CompanyReport[]): number[] {
    const values = records.filter(record => record.title.indexOf(',') === -1).map(record => record.percentage / 100);
    return this.selectedRecord > 0
      ? [
          values[this.selectedRecord],
          ...values.slice(0, this.selectedRecord),
          ...values.slice(this.selectedRecord + 1, values.length)
        ]
      : values;
  }

  formatPercentage(percentage: number): string {
    return percentage > 0.1 ? percentage.toFixed(2) : '~0.1';
  }

  formatDetailsValue(field: { key: string; value: number }): string {
    return 'nights' === field.key
      ? field.value.toString()
      : this.formatNumber(field.value, this.symbolType) + ' ' + this.symbolType;
  }

  startHeightAnimation(slider: HTMLElement, details: HTMLElement, statistics: HTMLElement) {
    const targetHeight = this.openedRecord > -1 ? details.offsetHeight : statistics.offsetHeight;
    this.sliderHeight = slider.clientHeight + 'px';
    this.sliderTargetHeight = targetHeight + 'px';
    this.sliderHeightState = 'on';
  }

  selectRecord(index: number) {
    if (this.showDetails || this.showOrigins) {
      this.openedRecord = index;
    }
    if (index === this.selectedRecord) {
      return;
    }
    this.doughnutDiveState = 'out';
    setTimeout(() => {
      this.doughnutDiveState = 'in';
      this.store.dispatch(new SelectReport({ index }));
    }, 400);
  }

  updatePdfRef(ref: DoughnutChartDirective) {
    this.pdf.setDoughnutChartRef(ref);
  }

  private updateFromState(state: DashboardCombinedState) {
    this.openedRecord = -1;
    this.symbolType = getFilterBySymbol(state);
    const turnover = state.options.filters.turnover;
    this.showDetails = turnover === 'AIR' && this.symbolType !== 'PNRs';
    this.showOrigins = state.config.config.origin_and_destination;
    this.grouping = state.options.grouping;
    this.originsAllRecordsTitle = this.grouping === 'consultants' ? this.dashboardAllConsultants :this.dashboardAllCarriers;
    this.hidden = true;
    setTimeout(() => (this.hidden = false), 0);
  }
}
